import React from "react";
import { useFormContext, Controller, FieldPath } from "react-hook-form";
import { Divider, Checkbox } from "@heroui/react";
import { Icon } from "@iconify/react";
import { createForm } from "src/components/forms/createForm";
import { bituahProviderOptions, TRequest } from "@shared/entities";
import { bituahProviderOptionsLabels } from "@shared/entities/utils";
import { useStore } from "src/store/store";
import { useTranslation } from "react-i18next";
import { AdminServiceFields } from "./AdminServiceFields";
const Form = createForm<TRequest>();

const addressFields: {
	label: string;
	name: FieldPath<TRequest>;
	icon: any;
	type?: string;
}[] = [
	{
		label: "ישוב",
		name: "clientDetails.city",
		type: "text",
		icon: null,
	},
	{
		label: "רחוב",
		name: "clientDetails.street",
		type: "text",
		icon: null,
	},
	{
		label: "מספר בית",
		name: "clientDetails.houseNumber",
		type: "text",
		icon: null,
	},
	{
		label: "דירה",
		name: "clientDetails.apartmentNumber",
		type: "text",
		icon: null,
	},
] as const;

const REQUEST_TYPE_OPTIONS = [
	{ label: "בקשה חדשה", value: "new" },
	{ label: "חידוש רישיון", value: "renew" },
	{ label: "הגדלת מינון", value: "increase" },
];
// A patient filing alone can pick only these indications; admins and brokers see them all.
const CLIENT_REASON_TYPES: string[] = ["chronicPain", "psychiatryPtsd"];

// A patient filing alone cannot open a new license — an admin or broker can.
const CLIENT_REQUEST_TYPE_OPTIONS = REQUEST_TYPE_OPTIONS.filter((o) => o.value !== "new");

export const PersonalInfoForm: React.FC = () => {
	const { control } = useFormContext();

	const store = useStore();
	const { t } = useTranslation();
	const { user } = store;
	// A patient filing alone gets the narrowed choices below; an admin or a
	// broker filing on a patient's behalf keeps every option.
	const isSelfService = !user?.admin && !user?.broker;

	const reasonOptions = [
		{
			label: t("reasonType.oncologyChemotherapy" as any),
			value: "oncologyChemotherapy",
		},
		{ label: t("reasonType.oncology" as any), value: "oncology" },
		{ label: t("reasonType.gastroCrohns" as any), value: "gastroCrohns" },
		{ label: t("reasonType.gastroColitis" as any), value: "gastroColitis" },
		{ label: t("reasonType.chronicPain" as any), value: "chronicPain" },
		{
			label: t("reasonType.infectiousDiseasesHiv" as any),
			value: "infectiousDiseasesHiv",
		},
		{
			label: t("reasonType.neuroMultipleSclerosis" as any),
			value: "neuroMultipleSclerosis",
		},
		{ label: t("reasonType.neuroParkinsons" as any), value: "neuroParkinsons" },
		{ label: t("reasonType.neuroTourettes" as any), value: "neuroTourettes" },
		{ label: t("reasonType.palliativeCare" as any), value: "palliativeCare" },
		{ label: t("reasonType.psychiatryPtsd" as any), value: "psychiatryPtsd" },
		{ label: t("reasonType.neuroEpilepsyAdults" as any), value: "neuroEpilepsyAdults" },
		{ label: t("reasonType.neuroEpilepsyMinors" as any), value: "neuroEpilepsyMinors" },
		{ label: t("reasonType.autism" as any), value: "autism" },
		{ label: t("reasonType.dementia" as any), value: "dementia" },
		{ label: t("reasonType.other" as any), value: "other" },
	];

	return (
		<div className="space-y-6 text-start">
			<div className="flex items-center gap-3 mb-4">
				<div className="bg-primary-100 p-2 rounded-full">
					<Icon icon="lucide:user" className="h-6 w-6 text-primary" />
				</div>
				<div>
					<h2 className="text-xl font-semibold">פרטים אישיים</h2>
					<p className="text-default-500 text-sm">הזן את פרטי המטופל</p>
				</div>
			</div>
			{user?.admin && <AdminServiceFields />}
			<Form.Select
				name="requestType"
				label="סוג בקשה"
				placeholder="בחר סוג בקשה"
				startContent={<Icon icon="lucide:file-text" className="h-4 w-4 text-default-400" />}
				options={isSelfService ? CLIENT_REQUEST_TYPE_OPTIONS : REQUEST_TYPE_OPTIONS}
			/>
			{/* The question is long: on a phone it wrapped inside the field's floating
			    label and spilled over the field above. It sits on its own line instead,
			    and the field keeps a short label. */}
			<p id="reasonTypeQuestion" className="text-sm font-medium -mb-3">
				{t("fields.reasonTypeQuestion" as any)}
			</p>
			<Form.Select
				name="reasonType"
				label="אינדיקציה רפואית"
				aria-describedby="reasonTypeQuestion"
				placeholder={t("fields.reasonTypePlaceholder" as any)}
				startContent={<Icon icon="lucide:heart" className="h-4 w-4 text-default-400" />}
				options={
					isSelfService
						? reasonOptions.filter((o) => CLIENT_REASON_TYPES.includes(o.value))
						: reasonOptions
				}
			/>{" "}
			{/* Show recommendation-only checkbox only for admin users */}
			{user?.admin && (
				<div className="flex flex-col gap-2">
					<Controller
						name="isRecommendationOnly"
						control={control}
						render={({ field }) => (
							<Checkbox {...field} isSelected={field.value} onValueChange={field.onChange}>
								בקשה להמלצה בלבד
							</Checkbox>
						)}
					/>
					<p className="text-sm text-default-500">
						בחר אם הבקשה היא להמלצה בלבד (אמייל לא יהיה חובה)
					</p>
				</div>
			)}
			<Divider />
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<Form.Input
					name="clientDetails.firstName"
					label="שם פרטי"
					placeholder="הכנס שם פרטי"
					startContent={<Icon icon="lucide:user" className="h-4 w-4 text-default-400" />}
				/>

				<Form.Input
					name="clientDetails.lastName"
					label="שם משפחה"
					placeholder="הכנס שם משפחה"
					startContent={<Icon icon="lucide:user" className="h-4 w-4 text-default-400" />}
				/>
				<Form.Input
					name="clientDetails.IDNumber"
					label="מספר תעודת זהות"
					placeholder="הכנס מספר תעודת זהות"
					inputMode="numeric"
					startContent={<Icon icon="lucide:id-card" className="h-4 w-4 text-default-400" />}
				/>
				<Form.Input
					name="clientDetails.dob"
					type="date"
					label="תאריך לידה"
					placeholder="בחר תאריך לידה"
				/>

				<Form.Select
					name="clientDetails.sex"
					options={[
						{ label: "זכר", value: "male" },
						{ label: "נקבה", value: "female" },
					]}
					label="מגדר"
					placeholder="בחר מגדר"
					startContent={<Icon icon="lucide:user" className="h-4 w-4 text-default-400" />}
				/>

				<Form.Input
					name="clientDetails.email"
					type="email"
					label="דוא״ל"
					placeholder="הכנס כתובת דוא״ל"
					autoComplete="email"
					startContent={<Icon icon="lucide:mail" className="h-4 w-4 text-default-400" />}
				/>
				<Form.Select
					name="clientDetails.healthInsurance"
					label="גורם מבטח"
					startContent={<Icon icon="lucide:file-text" className="h-4 w-4 text-default-400" />}
					options={bituahProviderOptions.map((o) => ({
						value: o,
						label: bituahProviderOptionsLabels[o],
					}))}
				/>
				<Form.Input
					name="clientDetails.phone"
					type="tel"
					label="טלפון"
					placeholder="הכנס מספר טלפון"
					autoComplete="tel"
					startContent={<Icon icon="lucide:phone" className="h-4 w-4 text-default-400" />}
				/>
				<Form.Input
					name="clientDetails.phone2"
					type="tel"
					label="טלפון נוסף"
					placeholder="הכנס מספר טלפון נוסף (אופציונלי)"
					startContent={<Icon icon="lucide:phone" className="h-4 w-4 text-default-400" />}
				/>
			</div>
			<div className="font-medium">כתובת</div>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{addressFields.map((field) => (
					<Form.Input
						key={field.name}
						{...field}
						startContent={<Icon icon="lucide:map-pin" className="h-4 w-4 text-default-400" />}
					/>
				))}
			</div>
		</div>
	);
};
